export type VatAmounts = Readonly<{
	inclusiveMinor: number;
	exclusiveMinor: number;
}>;

export type VatPresentation = 'included' | 'excluded';

/** Decode API decimal strings exactly; never round an authoritative amount. */
export function parseMoney(value: unknown): number {
	if (typeof value !== 'string' || value.length > 20) throw new TypeError('Invalid money.');
	const match = /^(0|[1-9]\d*)(?:\.(\d{1,2}))?$/.exec(value);
	if (!match) throw new TypeError('Invalid money.');
	const minor = BigInt(match[1]) * 100n + BigInt((match[2] ?? '').padEnd(2, '0'));
	if (minor > BigInt(Number.MAX_SAFE_INTEGER)) throw new RangeError('Money exceeds safe bounds.');
	return Number(minor);
}

/** Select the VAT basis only at the display boundary. */
export function moneyForDisplay(amount: VatAmounts, vat: VatPresentation = 'included') {
	return (vat === 'excluded' ? amount.exclusiveMinor : amount.inclusiveMinor) / 100;
}

/** Catalogue amounts can be absent on older API deployments or persisted products. */
export function optionalMoneyForDisplay(value: unknown): number | null {
	try {
		return parseMoney(value) / 100;
	} catch {
		return null;
	}
}

export function catalogPriceForDisplay(
	inclusive: number,
	exclusive: number | null | undefined,
	vat: VatPresentation = 'included'
): number | null {
	const amount = vat === 'excluded' ? exclusive : inclusive;
	return typeof amount === 'number' && Number.isFinite(amount) && amount >= 0 ? amount : null;
}

export function subtractMoney(total: VatAmounts, discount: VatAmounts): VatAmounts {
	const inclusiveMinor = total.inclusiveMinor - discount.inclusiveMinor;
	const exclusiveMinor = total.exclusiveMinor - discount.exclusiveMinor;
	if (inclusiveMinor < 0 || exclusiveMinor < 0 || exclusiveMinor > inclusiveMinor) {
		throw new RangeError('Invalid discounted total.');
	}
	return { inclusiveMinor, exclusiveMinor };
}
